import {
  FeedbackFormBuilder,
  ToggleRowBuilder,
  SelectRowBuilder,
  StarRowBuilder,
  TextAreaRowBuilder,
  IFeedbackFormMetadata,
  IFeedbackFormStructure,
  FeedbackRowType,
  ISelectRowStructure,
  ITextAreaRowStructure,
  IToggleRowStructure,
  IStarRowStructure,
  IBaseRowStructure,
  AbstractRowBuilder,
  FormCloseReason,
  IFrameCloseEvent,
} from '@wix/feedback-loop-structure';

import { FeedbackLoopFrameInjector } from './inject';
import { IFramePositionFunction } from './types';
import { shouldShowForm } from './api';

interface IFeedbackFormMetadataWithLang extends IFeedbackFormMetadata {
  language?: string;
}

export class FeedbackLoop {
  #builder: FeedbackFormBuilder;
  #injector = new FeedbackLoopFrameInjector();
  #metadata: IFeedbackFormMetadata;
  #language: string;

  constructor(
    private formId: string,
    { language, ...metadata }: IFeedbackFormMetadataWithLang,
  ) {
    this.#builder = new FeedbackFormBuilder(this.formId);
    this.#metadata = metadata;
    this.#language = language || 'en';
  }

  async #shouldShowForm() {
    return shouldShowForm(this.formId);
  }

  withTitle(title: string) {
    this.#builder.withTitle(title);
    return this;
  }

  withForm(cb: (builder: FeedbackFormBuilder) => void) {
    cb(this.#builder);
    return this;
  }

  withTextArea(id: string, cb: (builder: TextAreaRowBuilder) => void) {
    this.#builder.withTextArea(id, cb);
    return this;
  }
  withDropdown(id: string, cb: (builder: SelectRowBuilder) => void) {
    this.#builder.withDropdown(id, cb);
    return this;
  }
  withSegmentedToggle(id: string, cb: (builder: ToggleRowBuilder) => void) {
    this.#builder.withSegmentedToggle(id, cb);
    return this;
  }

  withFacesRatingBar(id: string, cb: (builder: StarRowBuilder) => void) {
    this.#builder.withFacesRatingBar(id, cb);
    return this;
  }

  async open(position?: IFramePositionFunction): Promise<
    | IFrameCloseEvent
    | {
        reason: 'not-shown';
      }
  > {
    if (!this.#builder.isValid()) {
      throw new Error('feedback-loop: form is not valid or not defined at all');
    }

    if (FeedbackLoopFrameInjector.isFeedbackFormIframeVisible()) {
      return Promise.resolve({
        reason: 'not-shown',
      });
    }

    if (await this.#shouldShowForm()) {
      return this.#injector.open(
        this.#builder.build(),
        this.#metadata,
        position,
        this.#language,
      );
    } else {
      return Promise.resolve({
        reason: 'not-shown',
      });
    }
  }

  async show() {
    this.#injector.show();
  }

  async hide() {
    this.#injector.hide();
  }

  async close() {
    this.#injector.close({
      reason: FormCloseReason.Closed,
      payload: [],
    });
  }

  _build() {
    return this.#builder.build();
  }
}

export function createFeedbackLoop(
  formId: string,
  metadata: IFeedbackFormMetadataWithLang,
) {
  return new FeedbackLoop(formId, metadata);
}

export function createFeedbackLoopFromStructure(
  metadata: IFeedbackFormMetadataWithLang,
  structure: IFeedbackFormStructure,
) {
  const form = new FeedbackLoop(structure.id, metadata);
  form.withTitle(structure.title);

  const withDefaults = (
    builder: AbstractRowBuilder<any>,
    row: IBaseRowStructure<any>,
  ) => {
    builder.withLabel(row.label);

    if (row.placeholder) {
      builder.withPlaceholder(row.placeholder);
    }
  };

  structure.rows.forEach((row) => {
    switch (row.type) {
      case FeedbackRowType.Select: {
        const _row = row as ISelectRowStructure;
        form.withDropdown(_row.id, (builder) => {
          withDefaults(builder, _row);

          _row.options?.forEach((option) => {
            builder.withOption(option.id, option.displayName);
          });
        });

        break;
      }

      case FeedbackRowType.TextArea: {
        const _row = row as ITextAreaRowStructure;
        form.withTextArea(_row.id, (builder) => {
          withDefaults(builder, _row);
        });

        break;
      }

      case FeedbackRowType.Toggle: {
        const _row = row as IToggleRowStructure;
        form.withSegmentedToggle(_row.id, (builder) => {
          withDefaults(builder, _row);

          const positive = _row.buttons?.find(
            (button) => button.id === 'positive', // TODO: move to const
          );
          const negative = _row.buttons?.find(
            (button) => button.id === 'negative', // TODO: move to const
          );

          if (positive) {
            builder.withPositiveLabel(positive.displayName);
          }

          if (negative) {
            builder.withNegativeLabel(negative.displayName);
          }
        });

        break;
      }

      case FeedbackRowType.Star: {
        const _row = row as IStarRowStructure;
        form.withFacesRatingBar(_row.id, (builder) => {
          withDefaults(builder, _row);

          if (_row.tooltips && _row.tooltips.length) {
            builder.withTooltips(_row.tooltips);
          }
        });

        break;
      }
    }
  });

  return form;
}
